import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Phone, Zap, CheckCircle2, MapPin } from "lucide-react";
import { SERVICES_DATA } from "../data/services";
import { NEIGHBORHOODS } from "../data/neighborhoods";
import { FAQ, STEPS } from "../data/faq";
import { TEL_LINK, waServiceLink, PHONE_FORMATTED } from "../constants/contact";
import { Seo } from "../components/Seo";
import { OpenStatus } from "../components/OpenStatus";
import { Footer } from "../components/Footer";
import { FadeIn } from "../components/FadeIn";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

export default function ServicePage() {
  const { slug } = useParams();
  const s = SERVICES_DATA.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/" replace />;

  const ld = JSON.stringify([
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: s.title,
      provider: {
        "@type": "Electrician",
        name: "Beylikdüzü Elektrikçi",
        telephone: "+905309562770",
        url: "https://ustayiz.org/",
      },
      areaServed: "Beylikdüzü, İstanbul",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ]);

  return (
    <>
      <Seo
        title={`Beylikdüzü ${s.shortTitle} | Usta Elektrik Servisi`}
        description={s.meta}
      />
      <Helmet>
        <script type="application/ld+json">{ld}</script>
      </Helmet>

      <section data-testid="service-page" className="relative pt-32 sm:pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid" aria-hidden="true" />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-amber-500/12 blur-[130px]"
        />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 mb-5" data-testid="service-breadcrumb">
              <Link to="/" className="hover:text-amber-400 transition-colors">Beylikdüzü Elektrikçi</Link>
              <span className="mx-2 text-slate-700">/</span>
              <span className="text-amber-400">{s.title}</span>
            </p>
            <div className="mb-6">
              <OpenStatus />
            </div>
            <h1
              data-testid="service-title"
              className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-slate-50"
            >
              Beylikdüzü <span className="text-amber-400">{s.shortTitle}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
              {s.pageDesc}
            </p>
            <p className="mt-4 max-w-2xl text-base text-slate-500 leading-relaxed" data-testid="service-extra">
              {s.extra}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href={TEL_LINK}
                data-testid="service-call-button"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-display font-bold text-base sm:text-lg px-8 py-4 transition-colors duration-200 shadow-[0_0_36px_rgba(245,158,11,0.4)]"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                HEMEN ARA: {PHONE_FORMATTED}
              </a>
              <a
                href={waServiceLink(s.title)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-display font-bold text-base sm:text-lg px-8 py-4 transition-colors duration-200"
              >
                <WhatsAppIcon className="w-5 h-5" />
                WHATSAPP İLE YAZ
              </a>
            </div>
          </motion.div>

          <FadeIn delay={0.1} className="mt-16">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-50 mb-6">
              Bu hizmetin kapsamı
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="service-scope">
              {s.scope.map((item, i) => (
                <div
                  key={item}
                  data-testid={`service-scope-${i}`}
                  className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-[#0F172A] px-5 py-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" strokeWidth={2.25} />
                  <span className="text-slate-200 font-medium text-sm sm:text-base">{item}</span>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="mt-16">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-50 mb-6">
              Nasıl çalışıyoruz?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="service-steps">
              {STEPS.map(({ icon: Icon, title, desc }, i) => (
                <div
                  key={title}
                  data-testid={`service-step-${i}`}
                  className="rounded-2xl border border-slate-800 bg-[#0F172A] p-6"
                >
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-4">
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </span>
                  <h3 className="font-display font-semibold text-slate-50">{title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="mt-16">
            <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <h2 className="font-display font-bold tracking-tight text-xl sm:text-2xl text-slate-50">
                  {s.shortTitle} için bugün gelelim
                </h2>
                <p className="mt-2 text-sm sm:text-base text-slate-400">
                  Arayın, ücreti işe başlamadan konuşalım. Telefonu doğrudan usta açar.
                </p>
              </div>
              <a
                href={TEL_LINK}
                data-testid="service-cta-call"
                className="inline-flex shrink-0 items-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-display font-bold px-7 py-3.5 transition-colors duration-200"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                {PHONE_FORMATTED}
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.18} className="mt-16">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-50 mb-6">
              Sık sorulan sorular
            </h2>
            <div className="space-y-4" data-testid="service-faq">
              {FAQ.map((f, i) => (
                <div
                  key={i}
                  data-testid={`service-faq-${i}`}
                  className="rounded-2xl border border-slate-800 bg-[#0F172A] p-6"
                >
                  <h3 className="font-display font-semibold text-slate-50 text-base sm:text-lg">{f.q}</h3>
                  <p className="mt-2 text-sm sm:text-base text-slate-400 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="mt-16">
            <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-5">
              Diğer hizmetlerimiz
            </h2>
            <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="service-other-links">
              {SERVICES_DATA.filter((x) => x.slug !== slug).map((x) => (
                <Link
                  key={x.slug}
                  to={`/hizmet/${x.slug}`}
                  data-testid={`service-link-${x.slug}`}
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
                >
                  <Zap className="w-3 h-3 text-amber-500/60" fill="currentColor" />
                  {x.title}
                </Link>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.22} className="mt-12">
            <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-5">
              Hizmet bölgelerimiz
            </h2>
            <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="service-area-links">
              {NEIGHBORHOODS.map((x) => (
                <Link
                  key={x.slug}
                  to={`/mahalle/${x.slug}`}
                  data-testid={`service-area-link-${x.slug}`}
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
                >
                  <MapPin className="w-3 h-3 text-amber-500/60" strokeWidth={2.5} />
                  {x.short} Elektrikçi
                </Link>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
      <Footer />
    </>
  );
}
